import React from 'react';
import { Box, ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import { appleTokens as t, tint } from '../../../theme/appleTokens';
import { IconTile } from './Page';

/** Style de base d'une carte (blanc, bordure fine, coins 12px, ombre très légère). */
export const cardSx = {
  bgcolor: t.surface,
  border: '1px solid',
  borderColor: t.separator,
  borderRadius: t.radius.lg,
  // Pas d'ombre : sur le fond blanc de l'app, la bordure fine suffit à délimiter le bloc (rendu « à plat »).
  boxShadow: 'none',
};

/**
 * Carte standard : en-tête optionnel (icône, titre, sous-titre, actions) + corps.
 * `padding={false}` pour un tableau / une liste pleine largeur. `footer` : pied séparé.
 */
export function Card({ title, subtitle, icon, actions, children, padding = true, footer, sx, bodySx, ...rest }) {
  const hasHeader = Boolean(title || actions);
  return (
    <Box {...rest} sx={{ ...cardSx, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden', ...sx }}>
      {hasHeader ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 2.25, py: 1.5, minHeight: 52, boxSizing: 'border-box', borderBottom: '1px solid', borderColor: t.separator }}
        >
          {icon ? <Box sx={{ display: 'flex', color: t.secondaryLabel }}>{React.cloneElement(icon, { sx: { fontSize: 18 } })}</Box> : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title ? (
              <Typography component="h2" sx={{ ...t.type.cardTitle, color: t.label }} noWrap>
                {title}
              </Typography>
            ) : null}
            {subtitle ? <Typography sx={{ ...t.type.caption, fontWeight: 400, color: t.secondaryLabel }}>{subtitle}</Typography> : null}
          </Box>
          {actions ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              {actions}
            </Stack>
          ) : null}
        </Stack>
      ) : null}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...(padding ? { p: 2.25 } : null), ...bodySx }}>
        {children}
      </Box>
      {footer ? (
        <Box sx={{ px: 2.25, py: 1.5, borderTop: '1px solid', borderColor: t.separator, bgcolor: t.surfaceSunken }}>{footer}</Box>
      ) : null}
    </Box>
  );
}

/** Tuile KPI : pastille d'icône, libellé, valeur. Cliquable (filtre) si `onClick`, `active` = filtre appliqué. */
export function KpiCard({ icon, label, value, hint, color, onClick, active, loading, sx }) {
  const tone = color || t.label;
  const content = (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
      <IconTile icon={icon} color={tone} size={40} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ ...t.type.small, fontWeight: 500, color: t.secondaryLabel }} noWrap>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={70} height={30} />
        ) : (
          <Typography noWrap title={typeof value === 'string' ? value : undefined} sx={{ ...t.type.title, ...t.type.numeric, color: t.label }}>
            {value}
          </Typography>
        )}
        {hint ? <Typography sx={{ ...t.type.caption, fontWeight: 400, color: t.tertiaryLabel }}>{hint}</Typography> : null}
      </Box>
    </Stack>
  );
  const base = {
    ...cardSx,
    flex: '1 1 200px',
    p: 2,
    display: 'flex',
    textAlign: 'left',
    ...(active ? { borderColor: tone, boxShadow: `0 0 0 1px ${tone}` } : null),
    ...sx,
  };
  if (!onClick) return <Box sx={base}>{content}</Box>;
  return (
    <ButtonBase
      onClick={onClick}
      sx={{ ...base, transition: 'box-shadow 0.15s ease', '&:hover': { boxShadow: active ? `0 0 0 1px ${tone}` : t.shadow.raised } }}
    >
      {content}
    </ButtonBase>
  );
}

/** Rangée de KPI (retour à la ligne automatique). */
export function KpiRow({ children }) {
  return (
    <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
      {children}
    </Stack>
  );
}

/** Pastille de statut : point + libellé, fond teinté. Toujours la même forme pour tous les statuts. */
export function StatusPill({ color = t.status.other.color, label, children, dot = true, sx }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        px: 1,
        height: 22,
        borderRadius: t.radius.pill,
        bgcolor: tint(color, 0.12),
        color,
        ...t.type.caption,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...sx,
      }}
    >
      {dot ? <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} /> : null}
      {label ?? children}
    </Box>
  );
}

/** État vide centré : pastille, titre, aide, action. `iconColor` teinte la pastille (ex. vert « rien à traiter »). */
export function EmptyState({ icon, iconColor, title, hint, action, sx }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, px: 3, textAlign: 'center', ...sx }}>
      {icon ? (
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: iconColor ? tint(iconColor, 0.12) : t.surfaceHover,
            color: iconColor || t.tertiaryLabel,
            mb: 0.5,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 22 } })}
        </Box>
      ) : null}
      <Typography sx={{ ...t.type.bodyStrong, color: t.label }}>{title}</Typography>
      {hint ? <Typography sx={{ ...t.type.small, color: t.secondaryLabel, maxWidth: 360 }}>{hint}</Typography> : null}
      {action ? <Box sx={{ pt: 1 }}>{action}</Box> : null}
    </Stack>
  );
}

/** Libellé de groupe en petites capitales (au-dessus d'un bloc de champs / d'une liste). */
export function SectionLabel({ children, sx }) {
  return <Typography sx={{ ...t.type.overline, color: t.tertiaryLabel, ...sx }}>{children}</Typography>;
}

/** Paire libellé / valeur (fiches de détail). */
export function InfoRow({ label, value, sx }) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="baseline" sx={{ py: 0.75, ...sx }}>
      <Typography sx={{ ...t.type.small, color: t.secondaryLabel, flexShrink: 0 }}>{label}</Typography>
      <Typography component="div" sx={{ ...t.type.smallStrong, color: t.label, textAlign: 'right', minWidth: 0 }}>
        {value}
      </Typography>
    </Stack>
  );
}
